import throttle from 'lodash.throttle';
import { useEffect, useRef, useState } from 'react';
import { Dropdown } from '../Dropdown/Dropdown';
import { SVG } from '../Dropdown/Dropdown.styles';
import {
  Label,
  LI,
  UL,
  Wrapper,
  DropdownButton,
  SelectedValue,
  OptionLabel,
} from './RoundedSelect.styles';
import { ISElectProps } from './Types';

export function RoundedSelect({
  className,
  buttonStyle,
  optionLabelStyle,
  popperStyle,
  label,
  options,
  onChange,
  placeholder,
  value,
}: ISElectProps & {
  buttonStyle?: any;
  optionLabelStyle?: any;
  popperStyle?: any;
}): JSX.Element {
  const [buttonRef, setButtonReference] = useState<HTMLButtonElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);
  const ulRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const handleResize = throttle(() => {
      if (window && ulRef?.current) {
        const dimensions = ulRef?.current?.getBoundingClientRect();

        const distanceToBottom = window?.innerHeight - dimensions.bottom;
        const distanceToTop = dimensions.top;
        const newMaxHeigh =
          distanceToBottom > distanceToTop ? distanceToBottom : distanceToTop;
        setMaxHeight(Math.floor(newMaxHeigh));
      }
    });
    handleResize();
    window.addEventListener('resize', handleResize);
    document.addEventListener('scroll', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('scroll', handleResize);
    };
  }, [ulRef]);

  return (
    <Wrapper className={className}>
      {label && <Label>{label}</Label>}

      <Dropdown
        onClose={() => setExpanded(false)}
        expanded={expanded}
        buttonReference={buttonRef}
        buttonElement={(state) => (
          <DropdownButton
            className={className}
            style={buttonStyle}
            ref={setButtonReference}
            onClick={() => setExpanded(!expanded)}
            placement={state?.placement as 'bottom-start' | 'top-start'}
            expanded={expanded}
          >
            <SelectedValue>{value?.label || placeholder}</SelectedValue>
            <SVG
              width="13"
              height="11"
              viewBox="0 0 13 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.63398 10.5C6.01888 11.1667 6.98113 11.1667 7.36603 10.5L12.1292 2.25C12.5141 1.58333 12.0329 0.75 11.2631 0.75H1.73686C0.967059 0.75 0.485935 1.58333 0.870835 2.25L5.63398 10.5Z"
                fill="#5FA3A9"
              />
            </SVG>
          </DropdownButton>
        )}
        popperStyle={popperStyle}
        popperElement={(state) => (
          <UL
            placement={state?.placement as 'top-start' | 'bottom-start'}
            expanded={expanded}
            ref={ulRef}
            maxHeight={maxHeight}
          >
            {options?.map((option) => (
              <LI
                key={option.value}
                $selected={value?.value === option.value}
                onClick={() => {
                  onChange(option);
                  setExpanded(false);
                }}
                placement={state?.placement as 'top-start' | 'bottom-start'}
              >
                <OptionLabel style={optionLabelStyle}>
                  {option.label}
                </OptionLabel>
              </LI>
            ))}
          </UL>
        )}
      />
    </Wrapper>
  );
}

RoundedSelect.defaultProps = {
  placeholder: '',
  className: '',
  value: null,
  containerBgType: 'light',
};
